import { Box } from "@itandi/itandi-bb-ui/Box";
import type React from "react";
import { useSWRConfig } from "swr";
import { ReportImageNavigation } from "~/components/common/reports/navigation/ReportImageNavigation";
import { StatusHandleClientButtons } from "~/components/common/statuses/StatusHandleClientButtons";
import { UploadFileNavigation } from "~/components/common/upload_files/UploadFileNavigation";
import { useDealContext } from "~/context/DealContext";
import { useDealCompletionReportImages } from "~/hooks/deals/useDealCompletionReportImages";
import { dealCacheKey } from "~/hooks/useDeals";
import useItandiAccount from "~/hooks/useItandiAccount";
import type { NskResponse } from "~/models/Api";
import type { DealUploadFile } from "~/models/DealUploadFile";
import { AWAITING_ACCEPTANCE, SEND_ESTIMATE } from "~/models/Status";
import { PATH as COMPLETION_REPORT_PATH } from "~/pages/deals/[id]/completion_reports";
import {
	getDownloadPath,
	create as uploadReceiptFile
} from "~/utils/api/deals/DealReceiptFile";
import {
	destroy as destroyReceiptFile,
	getFilePath
} from "~/utils/api/deals/DealUploadedFile";
import Styles from "./statuses.module.scss";

export const InConstruction: React.FC = () => {
	const deal = useDealContext();
	const { mutate: globalMutate } = useSWRConfig();
	const { data } = useItandiAccount().currentAccount();
	const { data: completeImages } = useDealCompletionReportImages().index(
		deal.id
	);
	const handleDestroyReceipt = async (
		file: DealUploadFile
	): Promise<NskResponse> => destroyReceiptFile(deal.id, file.id);
	const handleUploadReceipt = async (file: File): Promise<NskResponse> =>
		uploadReceiptFile(file, deal.id);
	const handleCreateReceipt = async (): Promise<NskResponse> =>
		uploadReceiptFile(null, deal.id);
	const handleGetReceiptPath = (file: DealUploadFile): string =>
		getFilePath(deal.id, file);
	const downloadExcel = (): void => {
		const path = getDownloadPath(deal.id);
		window.open(path);
	};

	if (data === null || completeImages === null) return null;

	return (
		<div>
			<h3 className={Styles.Title}>工事を依頼しました</h3>
			<Box marginY={16}>
				<p>工事が完了して、報告書が提出されるまでお待ち下さい。</p>
			</Box>
			<UploadFileNavigation
				deal={deal}
				description="登録されている工事発注書・請書は必要に応じてお手続きにご利用ください"
				downloadExcel={downloadExcel}
				files={deal.dealReceiptFiles}
				fileType="工事発注書・請書"
				handleCreate={handleCreateReceipt}
				handleDestroy={handleDestroyReceipt}
				handleGetFilePath={handleGetReceiptPath}
				handleMutate={async (): Promise<void> => {
					await globalMutate(dealCacheKey(deal.id));
				}}
				handleUpload={handleUploadReceipt}
				title="工事発注書・請書を登録してください"
			/>
			<ReportImageNavigation
				detailPageButtonLabel="画像登録画面"
				detailPagePath={COMPLETION_REPORT_PATH(deal.id)}
				optionalLabel
				reportImages={completeImages}
				title="完了報告画像を登録してください"
			/>
			<StatusHandleClientButtons
				forwardDisabled
				nextStatusId={AWAITING_ACCEPTANCE}
				prevStatusId={data.isAdmin ? SEND_ESTIMATE : null}
			/>
		</div>
	);
};
